import { useMemo } from "react";
import useSWR from "swr";
import type { App } from "@/types/domain";
import { swrKey, type User } from "@/lib/swr";

// Archived Apps retain their data permissions and metadata for historical links.
export const APP_CATALOG_KEY = swrKey("/apps", { includeArchived: true });
export const useAppCatalog = () => useSWR<App[]>(APP_CATALOG_KEY);

// App list scoped to the current user's visibility (for sidebar/selector; includes admin, narrowed by binding).
export const useApps = (): App[] => {
  const { data } = useSWR<App[]>(["/apps"]);
  return useMemo(() => (data || []).filter((app) => !app.archived), [data]);
};

// Visible App ids as strings; undefined until the catalog has loaded, so callers
// can wait instead of acting on an empty list.
export const useVisibleAppIds = (): Set<string> | undefined => {
  const { data } = useAppCatalog();
  return useMemo(() => (data ? new Set(data.map((a) => String(a.id))) : undefined), [data]);
};

// One app from the visible list by id (id arrives as string from the route,
// as number from some payloads — compare stringified).
export const useApp = (id: string | number | undefined): App | undefined => {
  const { data: apps } = useAppCatalog();
  if (id == null) return undefined;
  return apps?.find((a) => String(a.id) === String(id));
};

// Narrow to real client apps (isApp mark) for client-only features' pickers
// (e.g. RC). Falls back to the full list while no app has been marked yet,
// so pickers never go empty before the manual labeling pass.
export const clientApps = (apps: App[]): App[] => {
  const marked = apps.filter((a) => a.isApp);
  return marked.length ? marked : apps;
};

// Active admin catalog for assignment pickers; recovery views use /apps/all directly.
export const useAllApps = (): App[] => {
  const { data } = useSWR<App[]>(["/apps/all"]);
  return useMemo(() => (data || []).filter((app) => !app.archived), [data]);
};

// Members bound to an app via user_visible_app (readable by anyone who can
// see the app; used by member management and people pickers).
export const useAppMembers = (appId: string | number) => {
  const { data, isLoading } = useSWR<User[]>(
    appId ? [`/apps/${appId}/members`] : null
  );
  return { members: data || [], isLoading };
};
